import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Inline alert box used across the auth flow for error / success /
 * warning / info banners. Sits inside cards or above forms.
 *
 * Variants:
 *  - error    : red — invalid credentials, expired token, etc.
 *  - success  : green — "Password updated" confirmation banner.
 *  - warning  : amber — "Recovery code will trigger an alert email".
 *  - info     : neutral graphite — informational notices.
 *
 * Icons come from lucide-react (AlertCircle / CheckCircle /
 * AlertTriangle / Info). They render as 20×20 px (w-5 h-5) line-art
 * glyphs in the variant's text colour via currentColor — no filled
 * background circle, so the icon matches a finance-app aesthetic
 * instead of a Bootstrap-style alert.
 *
 * Accessibility:
 *  - Errors render with `role="alert"` so screen readers announce them.
 *  - Other variants are regions; their content is not necessarily
 *    breaking news.
 *  - Icons themselves are aria-hidden — the alert text carries the
 *    semantic content.
 */
type Variant = 'error' | 'success' | 'warning' | 'info';

type Props = {
  variant: Variant;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
};

const STYLE: Record<Variant, { bg: string; text: string; Icon: typeof AlertCircle }> = {
  error: { bg: 'bg-bg-alert', text: 'text-semantic-error', Icon: AlertCircle },
  success: { bg: 'bg-bg-success', text: 'text-semantic-success', Icon: CheckCircle },
  warning: { bg: 'bg-bg-alert', text: 'text-semantic-warning', Icon: AlertTriangle },
  info: { bg: 'bg-glacier', text: 'text-ink', Icon: Info },
};

export function Alert({ variant, title, children, className }: Props) {
  const s = STYLE[variant];
  const Icon = s.Icon;
  return (
    <div
      role={variant === 'error' ? 'alert' : 'region'}
      className={`${s.bg} ${s.text} rounded-card px-4 py-3 flex gap-3 items-start ${className ?? ''}`}
    >
      <Icon strokeWidth={2} aria-hidden="true" className="flex-shrink-0 w-5 h-5 mt-0.5" />
      <div className="flex-1 text-sm leading-relaxed">
        {title ? <div className="font-medium">{title}</div> : null}
        {children ? <div className={title ? 'mt-0.5' : ''}>{children}</div> : null}
      </div>
    </div>
  );
}
